import React, { useEffect, useRef } from 'react';
import { ExperienceList } from './ExperienceList';
function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => el.classList.add('in-view'), delay);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}
const AVATAR = 'https://cdn.magicpatterns.com/uploads/7aMBMbUATZHD4MERUUdtkd/image.png';
const avatarStyle: React.CSSProperties = {
  objectFit: 'cover',
  objectPosition: 'center 20%',
  border: '1px solid var(--border)',
  background: '#FFFFFF'
};
const highlights = [{
  value: '$30M+',
  label: 'Order volume run through systems I redesigned'
}, {
  value: '~90%',
  label: 'Faster approvals with live AI-built prototypes'
}];
const facts = [{
  label: 'Now',
  items: ['MBA Candidate, IESE Business School', 'Product Advisory Board, Narra Health']
}, {
  label: 'Education',
  items: ['BA Design, Service Design', 'Universidad de Navarra']
}, {
  label: 'Languages',
  items: ['English, Filipino, Spanish (C2)']
}];
export function About() {
  const bioRef = useReveal();
  const statsRef = useReveal(120);
  return <section id="about" className="w-full py-20 md:py-32" style={{
    background: 'var(--bg)'
  }} data-id="element-6" data-mp="35yfdt51mtw6">
      <div className="content-wrap px-6 md:px-12" data-id="element-7" data-mp="3uc9e3imkzikc">
        <h2 className="text-4xl md:text-6xl tracking-tight mb-10 md:mb-12" style={{
        color: 'var(--fg)'
      }} data-id="element-8" data-mp="3ubhpirreil3k">
          About me
        </h2>

        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-12" data-id="element-9" data-mp="3lbkyijhjha5c">
          {/* Bio + facts */}
          <div ref={bioRef} className="reveal lg:col-span-7" data-id="element-10" data-mp="1nltiyf9al90d">
            <div className="md:flex md:items-start md:gap-4" data-id="element-11" data-mp="1nl0gvrla37ok">
              <img src={AVATAR} alt="Stella Pangilinan" className="hidden h-16 w-16 flex-shrink-0 rounded-full md:block" style={avatarStyle} data-id="element-12" data-mp="1jjygv8jjerlj" />
              <div className="min-w-0 flex-1" data-id="element-13" data-mp="3hufbi9x8q0gc">
                <p className="mb-3 flex items-center gap-3 md:mb-2 md:items-baseline md:gap-2" data-id="element-14" data-mp="3429174grqnot">
                  <img src={AVATAR} alt="Stella Pangilinan" className="h-11 w-11 flex-shrink-0 rounded-full md:hidden" style={avatarStyle} data-id="element-15" data-mp="383u7u82pm5qk" />
                  <span className="font-aspekta text-base" style={{
                  color: 'var(--fg)'
                }} data-id="element-16" data-mp="4m2lpab98g0a">
                    Stella Pangilinan
                  </span>
                  <span className="text-sm" style={{
                  color: 'var(--muted)'
                }} data-id="element-17" data-mp="3rrtilix9xcvz">
                    Barcelona
                  </span>
                </p>
                <div className="rounded-3xl rounded-tl-md p-5 sm:p-6 md:p-8" style={{
                background: 'var(--card)',
                border: '1px solid var(--border)'
              }} data-id="element-18" data-mp="2bcsb4dgmfivr">
                  <p className="text-lg md:text-xl leading-relaxed mb-5" style={{
                  color: 'var(--fg)'
                }} data-id="element-19" data-mp="27u2w5g1b09p5">
                    Stella Pangilinan is a product designer and strategist who helps
                    founders take ideas from concept to capital, with seven years of
                    building across MedTech, TeleHealth, e-commerce, and operations.
                    After starting her
                    career in MedTech as Lead Product Designer at KevinRoot Medical,
                    she founded GoLaunch, a product studio partnering with
                    early-stage companies across Spain, the Philippines, Singapore,
                    and the UK. Her portfolio companies have raised over $4M, and she
                    serves as Product Advisor at Narra Health.
                  </p>
                  <p className="text-lg md:text-xl leading-relaxed" style={{
                  color: 'var(--muted)'
                }} data-id="element-20" data-mp="16cu30zegoy1j">
                    Now an MBA candidate at IESE Business School, Stella helps teams
                    turn complex problems into clear, usable products, and products
                    into businesses.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Impact */}
          <div ref={statsRef} className="reveal lg:col-span-4 lg:col-start-9 lg:pt-8" data-id="element-21" data-mp="iflgbt60o0oi">
            <div className="pb-5" style={{
            borderBottom: '1px solid var(--border)'
          }} data-id="element-22" data-mp="ihg37d0um2v8">
              <p className="font-aspekta text-5xl sm:text-6xl md:text-7xl tracking-tight" style={{
              color: 'var(--fg)'
            }} data-id="element-23" data-mp="ngsohtc2n1vp">
                $4M+
              </p>
              <p className="font-aspekta mt-2 text-base" style={{
              color: 'var(--muted)'
            }} data-id="element-24" data-mp="xdxe6354lfh9">
                raised by portfolio companies
              </p>
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-6 pt-5" data-id="element-25" data-mp="2z4iljbtxrfke">
              {highlights.map(item => <div key={item.value} data-id="element-26" data-mp="3352fy7n4k52u">
                  <dt className="sr-only" data-id="element-27" data-mp="33644nex2cq31">{item.label}</dt>
                  <dd data-id="element-28" data-mp="2vml1ro4b023j">
                    <span className="block font-aspekta text-3xl tracking-tight" style={{
                  color: 'var(--fg)'
                }} data-id="element-29" data-mp="2vn3492hk7cjw">
                      {item.value}
                    </span>
                    <span className="font-aspekta mt-1 block text-sm leading-snug" style={{
                  color: 'var(--muted)'
                }} data-id="element-30" data-mp="2lvqgon2sn8jo">
                      {item.label}
                    </span>
                  </dd>
                </div>)}
            </dl>

            <dl className="mt-6" style={{
            borderTop: '1px solid var(--border)'
          }} data-id="element-31" data-mp="3eitbb44cmz4n">
              {facts.map(fact => <div key={fact.label} className="grid grid-cols-[96px_1fr] gap-4 py-3" style={{
              borderBottom: '1px solid var(--border)'
            }} data-id="element-32" data-mp="35k8zmmx7hppx">
                  <dt className="font-aspekta text-sm pt-0.5" style={{
                color: 'var(--muted)'
              }} data-id="element-33" data-mp="1u5vgtwhb80gr">
                    {fact.label}
                  </dt>
                  <dd className="text-base leading-snug" style={{
                color: 'var(--fg)'
              }} data-id="element-34" data-mp="1p8bzuot899l4">
                    {fact.items.map(item => <span key={item} className="block" data-id="element-35" data-mp="1vn141yr7x3ai">
                        {item}
                      </span>)}
                  </dd>
                </div>)}
            </dl>
          </div>
        </div>

        <ExperienceList data-id="element-36" data-mp="1lmqs0hgx61wx" />
      </div>
    </section>;
}